import { useState } from "react";
import { Moon, RefreshCw, Sun } from "lucide-react";
import { IconButton, useTheme, type ThemeMode } from "@studio";
import { fmtTime } from "../lib/format";
import { useRealtime } from "../lib/realtime";

/**
 * 시안 6a084bb: 연결 상태는 전역 사이드바가 아니라 지표 바로 위에 둔다 (DataFreshness).
 * 실시간 채널이 붙어 있으면 "실시간 반영", 끊겼으면 "주기 갱신 중". 시각은 마지막 신호(없으면 화면이 그린 시각).
 */
export function Freshness() {
  const live = useRealtime((s) => s.connected);
  const lastEventAt = useRealtime((s) => s.lastEventAt);
  const [rendered] = useState(() => Date.now());
  return (
    <div className={`lwi-freshness${live ? "" : " is-polling"}`} role="status">
      <span className="lwi-freshness-mode">
        <RefreshCw size={13} aria-hidden="true" />
        {live ? "실시간 반영" : "주기 갱신 중"}
      </span>
      <span className="lwi-freshness-time">
        마지막 갱신 <time>{fmtTime(new Date(lastEventAt ?? rendered).toISOString())}</time>
      </span>
    </div>
  );
}

/** 지표 묶음 제목 + 갱신 정보 한 줄. */
export function DataHeading({ title }: { title: string }) {
  return (
    <div className="lwi-data-heading">
      <strong>{title}</strong>
      <Freshness />
    </div>
  );
}

const THEME_KEY = "meta-crew.theme";
export function storedThemeMode(): ThemeMode | null {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v === "light" || v === "dark" || v === "system" ? v : null;
  } catch {
    return null;
  }
}

/** 테마를 바꾸고 이 브라우저(데스크톱 앱이면 이 PC)에 남긴다. "system" = OS 테마를 따름. */
export function useThemeMode() {
  const { mode, resolved, setMode } = useTheme();
  const save = (next: ThemeMode) => {
    setMode(next);
    try {
      localStorage.setItem(THEME_KEY, next);
    } catch {
      /* 비공개 창 등 */
    }
  };
  return { mode, resolved, save };
}

/** 라이트·다크 전환 (시안 다크 테마). 선택은 이 브라우저에만 남는다 (데스크톱 앱은 이 PC 에, 기본은 OS 테마). */
export function ThemeToggle() {
  const { resolved, save } = useThemeMode();
  const dark = resolved === "dark";
  return (
    <IconButton label={dark ? "라이트 테마로" : "다크 테마로"} onClick={() => save(dark ? "light" : "dark")}>
      {dark ? <Sun size={17} /> : <Moon size={17} />}
    </IconButton>
  );
}
